import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { useLanguage } from "@/lib/LanguageContext";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import FloatingWhatsApp from "@/components/FloatingWhatsApp";
import {
  ChevronLeft,
  History,
  ScrollText,
  FileBadge2,
  Building2,
  ShieldCheck,
  Award,
  Printer,
  Camera,
  Calendar,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ---------------------------------------------------------------
   Data — Sejarah RS Bhayangkara Tarakan (simulasi resmi Polri)
   Semua tanggal & sertifikat berikut adalah simulasi dan dapat
   diedit sesuai catatan resmi Rumah Sakit Bhayangkara Tarakan.
   --------------------------------------------------------------- */
const DEFAULT_TIMELINE = [
  {
    year: "2005",
    title: "Poliklinik Kesehatan Polri",
    body:
      "Cikal-bakal berdiri sebagai poliklinik kesehatan yang melayani anggota Polri dan keluarganya di lingkungan Polda Kaltara.",
  },
  {
    year: "2010",
    title: "Peningkatan Status Menjadi Balai Pengobatan",
    body:
      "Status ditingkatkan menjadi Balai Pengobatan tingkat lanjut dengan penambahan poli umum, poli gigi, dan farmasi.",
  },
  {
    year: "2015",
    title: "Berdiri sebagai Rumah Sakit Bhayangkara",
    body:
      "Diresmikan sebagai Rumah Sakit Bhayangkara Tarakan Tk. IV berdasarkan Peraturan Kapolri No. XX/2015, melayani anggota Polri, keluarga, ASN Polri dan masyarakat umum.",
  },
  {
    year: "2018",
    title: "Lulus Akreditasi Nasional",
    body:
      "Lulus Akreditasi Rumah Sakit dari KARS (Komisi Akreditasi Rumah Sakit) dengan predikat Perdana.",
  },
  {
    year: "2021",
    title: "Peningkatan Kelas Menjadi Tipe III",
    body:
      "SK Kemenkes RI menetapkan RS Bhayangkara Tarakan naik status ke Tipe III dengan fasilitas rawat inap 60+ tempat tidur.",
  },
  {
    year: "2024",
    title: "Akreditasi Paripurna & Layanan Terpadu",
    body:
      "Meraih akreditasi Paripurna (bintang 5) LARS-DHP. Melengkapi layanan poli spesialis, laboratorium, radiologi CT-Scan, ICU, dan kamar operasi mayor.",
  },
];

const DEFAULT_PERIZINAN = [
  {
    icon: "FileBadge2",
    title: "Izin Operasional",
    detail: "SK Kemenkes RI No. HK.01.07/MENKES/2021",
    valid: "Berlaku 5 tahun • diperpanjang berkala",
  },
  {
    icon: "ShieldCheck",
    title: "SK Pendirian",
    detail: "Peraturan Kapolri No. XX Tahun 2015",
    valid: "Ditetapkan di Jakarta • 2015",
  },
  {
    icon: "Award",
    title: "Akreditasi",
    detail: "LARS-DHP • Paripurna (Bintang 5)",
    valid: "Berlaku 2024 – 2027",
  },
  {
    icon: "Building2",
    title: "NIB & OSS",
    detail: "Nomor Induk Berusaha aktif via OSS-RBA",
    valid: "Terintegrasi Kemenkes",
  },
];

const ICON_MAP = { FileBadge2, ShieldCheck, Award, Building2 };

const DEFAULT_KLASIFIKASI = {
  tipe: "Rumah Sakit Umum Kelas C (Tipe III Polri)",
  kepemilikan: "Kepolisian Negara Republik Indonesia (Polri)",
  jenis: "Rumah Sakit Umum",
  kapasitas: "60+ Tempat Tidur",
  igd: "24 Jam / 7 Hari",
  layanan: [
    "Pelayanan Medik Umum",
    "Pelayanan Gawat Darurat",
    "Pelayanan Medik Spesialis Dasar",
    "Pelayanan Penunjang Medik",
    "Pelayanan Keperawatan & Kebidanan",
    "Pelayanan Farmasi",
    "Pelayanan Rehabilitasi Medik",
  ],
};

const DEFAULT_OPENING_PHOTOS = [
  {
    id: "ceremony-1",
    src: "https://images.unsplash.com/photo-1587351021355-a479a299d2f9?w=1200&q=80&auto=format&fit=crop",
    caption: "Peresmian Gedung Utama RS Bhayangkara Tarakan — 2015",
    subtitle: "Penandatanganan prasasti oleh Kapolda Kaltara",
  },
  {
    id: "ceremony-2",
    src: "https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?w=1200&q=80&auto=format&fit=crop",
    caption: "Pemotongan Pita Peresmian",
    subtitle: "Bersama unsur Muspida Kota Tarakan",
  },
  {
    id: "ceremony-3",
    src: "https://images.unsplash.com/photo-1631815589968-fdb09a223b1e?w=1200&q=80&auto=format&fit=crop",
    caption: "Prasasti Peresmian Rumah Sakit",
    subtitle: "Batu prasasti bertuliskan tanggal peresmian",
  },
];

const DEFAULT_NARASI =
  "Rumah Sakit Bhayangkara Tarakan adalah rumah sakit umum milik Kepolisian Negara Republik Indonesia (Polri) yang berada di bawah naungan Polda Kalimantan Utara. Berdiri di Kota Tarakan sebagai jawaban atas kebutuhan pelayanan kesehatan bagi anggota Polri, Aparatur Sipil Negara (ASN) Polri, keluarga, serta masyarakat umum Kalimantan Utara. Mengedepankan motto \"Melayani dengan Sepenuh Hati\", rumah sakit terus bertumbuh dari waktu ke waktu — dari poliklinik kesehatan sederhana menjadi rumah sakit terakreditasi Paripurna dengan layanan spesialis terpadu.";

const DEFAULT_CONTENT = {
  narasi: DEFAULT_NARASI,
  timeline: DEFAULT_TIMELINE,
  perizinan: DEFAULT_PERIZINAN,
  klasifikasi: DEFAULT_KLASIFIKASI,
  photos: DEFAULT_OPENING_PHOTOS,
};

const Sejarah = () => {
  const { t } = useLanguage();
  const [content, setContent] = useState(DEFAULT_CONTENT);
  const handlePrint = () => window.print();

  useEffect(() => {
    axios
      .get(`${API}/site-content/sejarah`)
      .then((r) => {
        if (r.data && r.data.doc) setContent({ ...DEFAULT_CONTENT, ...r.data.doc });
      })
      .catch(() => {
        /* keep defaults */
      });
  }, []);

  const TIMELINE = content.timeline || DEFAULT_TIMELINE;
  const PERIZINAN = content.perizinan || DEFAULT_PERIZINAN;
  const KLASIFIKASI = content.klasifikasi || DEFAULT_KLASIFIKASI;
  const OPENING_PHOTOS = content.photos || DEFAULT_OPENING_PHOTOS;
  const NARASI = content.narasi || DEFAULT_NARASI;

  return (
    <div className="min-h-screen flex flex-col bg-slate-50" data-testid="sejarah-page">
      <div className="no-print">
        <Header />
      </div>
      <main className="flex-1 pt-24 pb-16 print:pt-4">
        <div className="container-x">
          {/* Top navigation & print button */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-8 no-print">
            <div>
              <Link
                to="/"
                data-testid="sejarah-back-link"
                className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-blue-800 mb-2"
              >
                <ChevronLeft className="h-4 w-4" />
                {t.nav.home}
              </Link>
              <h1 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 flex items-center gap-2">
                <History className="h-7 w-7 text-blue-800" />
                Sejarah Rumah Sakit
              </h1>
              <p className="text-sm text-slate-500 mt-1">
                Perjalanan RS Bhayangkara Tarakan — dari poliklinik hingga rumah sakit
                terakreditasi Paripurna
              </p>
            </div>
            <button
              onClick={handlePrint}
              data-testid="sejarah-print-btn"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium bg-blue-800 hover:bg-blue-900 text-white shadow-md"
            >
              <Printer className="h-4 w-4" />
              Cetak
            </button>
          </div>

          {/* Print-only header */}
          <div className="hidden print:block mb-6 text-center border-b pb-4">
            <div className="text-lg font-bold">RS BHAYANGKARA TARAKAN</div>
            <div className="text-sm">Polda Kalimantan Utara</div>
            <div className="text-base font-semibold mt-2">SEJARAH & PERIZINAN</div>
          </div>

          {/* === SEKILAS SEJARAH === */}
          <section className="mb-12" data-testid="sejarah-timeline-section">
            <div className="flex items-center gap-2 mb-6">
              <ScrollText className="h-5 w-5 text-blue-800" />
              <h2 className="font-heading text-2xl font-bold text-slate-900">
                Sekilas Sejarah
              </h2>
            </div>

            <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-6 md:p-8 mb-6">
              <p className="text-slate-700 leading-relaxed whitespace-pre-line">
                {NARASI}
              </p>
            </div>

            {/* Timeline */}
            <div className="relative pl-6 md:pl-10">
              <div className="absolute left-2 md:left-4 top-2 bottom-2 w-0.5 bg-gradient-to-b from-blue-400 via-blue-600 to-blue-900 rounded-full" />
              <div className="space-y-6">
                {TIMELINE.map((ev, i) => (
                  <div
                    key={ev.year}
                    data-testid={`timeline-${ev.year}`}
                    className="relative"
                  >
                    <div className="absolute -left-6 md:-left-10 top-2 h-4 w-4 rounded-full bg-blue-800 border-4 border-white shadow-md" />
                    <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm hover:shadow-md transition">
                      <div className="flex items-center gap-2 mb-2">
                        <Calendar className="h-4 w-4 text-blue-800" />
                        <span className="font-mono text-xs font-bold text-blue-800 bg-blue-50 px-2 py-0.5 rounded-full">
                          {ev.year}
                        </span>
                      </div>
                      <h3 className="font-heading font-semibold text-slate-900 text-lg">
                        {ev.title}
                      </h3>
                      <p className="text-sm text-slate-600 mt-1 leading-relaxed">
                        {ev.body}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* === PERIZINAN === */}
          <section className="mb-12" data-testid="sejarah-perizinan-section">
            <div className="flex items-center gap-2 mb-6">
              <FileBadge2 className="h-5 w-5 text-blue-800" />
              <h2 className="font-heading text-2xl font-bold text-slate-900">Perizinan</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {PERIZINAN.map((p) => {
                const Icon = ICON_MAP[p.icon] || FileBadge2;
                return (
                  <div
                    key={p.title}
                    data-testid={`perizinan-${p.title.replace(/\s+/g, "-").toLowerCase()}`}
                    className="bg-white rounded-2xl border border-slate-100 p-5 flex items-start gap-4 shadow-sm hover:shadow-md transition"
                  >
                    <div className="h-11 w-11 shrink-0 rounded-xl bg-blue-50 text-blue-800 flex items-center justify-center">
                      <Icon className="h-5 w-5" />
                    </div>
                    <div className="min-w-0">
                      <div className="font-heading font-semibold text-slate-900">
                        {p.title}
                      </div>
                      <div className="text-sm text-slate-700 mt-0.5">{p.detail}</div>
                      <div className="text-xs text-slate-500 mt-1">{p.valid}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          {/* === TIPE / KLASIFIKASI === */}
          <section className="mb-12" data-testid="sejarah-klasifikasi-section">
            <div className="flex items-center gap-2 mb-6">
              <Building2 className="h-5 w-5 text-blue-800" />
              <h2 className="font-heading text-2xl font-bold text-slate-900">
                Tipe & Klasifikasi
              </h2>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
              {/* Featured card */}
              <div className="lg:col-span-1 rounded-3xl p-6 bg-gradient-to-br from-blue-800 to-blue-950 text-white shadow-lg">
                <div className="text-xs uppercase tracking-wider text-blue-200 mb-2">
                  Kelas Rumah Sakit
                </div>
                <div className="font-heading font-bold text-2xl leading-tight">
                  {KLASIFIKASI.tipe}
                </div>
                <div className="mt-6 pt-4 border-t border-white/20 space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-blue-200">Jenis</span>
                    <span className="font-medium">{KLASIFIKASI.jenis}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-blue-200">Kepemilikan</span>
                    <span className="font-medium text-right">Polri</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-blue-200">Kapasitas</span>
                    <span className="font-medium">{KLASIFIKASI.kapasitas}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-blue-200">IGD</span>
                    <span className="font-medium">{KLASIFIKASI.igd}</span>
                  </div>
                </div>
              </div>

              {/* Layanan list */}
              <div className="lg:col-span-2 bg-white rounded-3xl border border-slate-100 shadow-sm p-6">
                <div className="font-heading font-semibold text-slate-900 mb-4">
                  Layanan Kesehatan Sesuai Klasifikasi
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {KLASIFIKASI.layanan.map((l, i) => (
                    <div
                      key={l}
                      className="flex items-center gap-2 px-3 py-2 rounded-xl bg-blue-50/50 border border-blue-50 text-sm text-slate-700"
                    >
                      <div className="h-5 w-5 rounded-full bg-blue-800 text-white flex items-center justify-center text-[10px] font-bold shrink-0">
                        {i + 1}
                      </div>
                      {l}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* === FOTO PERESMIAN === */}
          <section className="mb-8" data-testid="sejarah-foto-section">
            <div className="flex items-center gap-2 mb-6">
              <Camera className="h-5 w-5 text-blue-800" />
              <h2 className="font-heading text-2xl font-bold text-slate-900">
                Foto Peresmian
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {OPENING_PHOTOS.map((p) => (
                <figure
                  key={p.id}
                  data-testid={`opening-photo-${p.id}`}
                  className="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden group"
                >
                  <div className="aspect-[4/3] overflow-hidden bg-slate-100">
                    <img
                      src={p.src}
                      alt={p.caption}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <figcaption className="p-4">
                    <div className="font-heading font-semibold text-slate-900 text-sm">
                      {p.caption}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">{p.subtitle}</div>
                  </figcaption>
                </figure>
              ))}
            </div>
            <p className="text-xs text-slate-500 mt-4 italic">
              Foto peresmian dapat diperbarui melalui menu Admin → Galeri. Foto yang
              ditampilkan saat ini bersifat ilustrasi.
            </p>
          </section>
        </div>
      </main>
      <div className="no-print">
        <Footer />
      </div>
      <div className="no-print">
        <FloatingWhatsApp />
      </div>
    </div>
  );
};

export default Sejarah;
